import { useGameStore } from '../stores/gameStore';

interface GameEndProps {
  onPlayAgain: () => void;
  onExit: () => void;
}

function GameEnd({ onPlayAgain, onExit }: GameEndProps) {
  const winner = useGameStore((s) => s.winner);
  const players = useGameStore((s) => s.players);
  const playerStats = useGameStore((s) => s.playerStats);
  const roundHistory = useGameStore((s) => s.roundHistory);
  const playerId = useGameStore((s) => s.playerId);

  const getPlayerName = (pid: string) => {
    const player = players.find((p) => p.id === pid);
    return player?.name || 'Unknown';
  };

  const scoreboard = [...players]
    .filter((p) => p.connectionType !== 'display')
    .map((p) => ({
      ...p,
      score: playerStats[p.id]?.score || 0,
      boringWins: playerStats[p.id]?.boringWins || 0,
      excitingWins: playerStats[p.id]?.excitingWins || 0,
    }))
    .sort((a, b) => b.score - a.score);

  const isWinner = winner?.id === playerId;
  const medals = ['\u{1F947}', '\u{1F948}', '\u{1F949}'];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-6">
        {/* Winner Banner */}
        <div className="text-center py-6">
          <div className="text-6xl mb-4">{'\u{1F3C6}'}</div>
          <h1 className="text-3xl font-black">
            {isWinner ? 'You Win!' : `${winner?.name || 'Game Over'} Wins!`}
          </h1>
          <p className="text-base-content/60 mt-2">
            {roundHistory.length} round{roundHistory.length !== 1 ? 's' : ''} played
          </p>
        </div>

        {/* Final Scoreboard */}
        <div className="card bg-base-200">
          <div className="card-body py-4">
            <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-2">
              Final Scores
            </h3>
            <div className="space-y-3">
              {scoreboard.map((p, i) => (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 py-2 px-3 rounded-lg ${
                    i === 0 ? 'bg-primary/10' : ''
                  }`}
                >
                  <span className="text-2xl w-8 text-center">
                    {medals[i] || ''}
                  </span>
                  <div className="flex-1">
                    <p className="font-bold">
                      {p.name}
                      {p.id === playerId ? ' (you)' : ''}
                    </p>
                    <p className="text-xs text-base-content/40">
                      {p.excitingWins} exciting · {p.boringWins} boring
                    </p>
                  </div>
                  <span className="text-2xl font-black text-primary">{p.score}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* All Dares from Game */}
        {roundHistory.length > 0 && (
          <div className="card bg-base-200">
            <div className="card-body py-4">
              <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-2">
                All Dares
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {roundHistory.map((round) =>
                  round.allSubmissions.map((sub) => (
                    <div key={`${round.roundNumber}-${sub.submissionId}`} className="text-sm py-1 border-b border-base-300 last:border-0">
                      <span className="text-base-content/40">R{round.roundNumber}</span>{' '}
                      <span className="font-medium">{getPlayerName(sub.playerId)}</span>:{' '}
                      <span className="italic">"{sub.dare}"</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button onClick={onPlayAgain} className="btn btn-primary btn-lg w-full">
            Play Again
          </button>
          <button onClick={onExit} className="btn btn-ghost w-full">
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default GameEnd;
